import { useColorMode } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'
import type { IGroupScope } from '@/types/Group'
import type { IGroupUser } from '@/types/User'

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const locale       = ref(localStorage.getItem('locale') || 'sl')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<IGroupUser>()

    /**
     * What this account may do. Never decided here: the console hides what it must not
     * offer and the server refuses it regardless — a hidden button is courtesy, not
     * security.
     *
     * Resolved WITHOUT a property, unlike the reception portal's: every right this
     * console draws itself from (`group:read`, `group:manage`, and the reporting rights
     * of the packages after this one) is held across the company, and asking about one
     * house would answer for one house.
     */
    const permissions = ref<string[]>([])

    /** What this account may SEE — the houses behind the scope selector. */
    const scope = ref<IGroupScope | null>(null)

    /** The read in flight, so several callers share one — see ensureUserData. */
    let userDataInFlight: Promise<void> | null = null

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() — never a
    // second source of truth. Values: 'auto' | 'light' | 'dark'.
    //
    // emitAuto is what makes 'auto' readable: without it VueUse resolves the stored
    // preference before handing it over, so somebody on 'auto' with a dark system reads
    // back as 'dark' — and the three-state button in the topbar could never leave.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme', emitAuto: true })

    window.addEventListener('resize', () => { windowWidth.value = window.innerWidth })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    /** Whether this account holds a permission across the company. */
    const can = (permission: string): boolean => permissions.value.includes(permission)

    /** Whether it holds any of them — for a screen several rights reach. */
    const canAny = (...wanted: string[]): boolean => wanted.some(can)

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value = ''
        userData.value = undefined
        permissions.value = []
        scope.value = null
        userDataInFlight = null
        clearServerSession()
    }

    /**
     * Signing out, and the cookie the server holds.
     *
     * The session cookie is written by the server, is HttpOnly, and is what authenticates
     * the deep link into a house (`libs/group.ts`, `openInReceptionUrl`) — so this console
     * has the same reason as the reception portal to ask the server to drop it, and the
     * same inability to do it itself. Asked and not waited on: whatever the network says,
     * this browser is signed out the moment somebody presses the button.
     */
    const clearServerSession = () => {
        void window.fetch('/api/pms/v1/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {})
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => { loaderActive.value = Math.max(0, loaderActive.value - 1) }

    /**
     * Who is signed in, what they may do and what they may see.
     *
     * Asked with no `property_id`: see `permissions` above. The scope may be absent from
     * the answer of a service that predates this work package, and absent is not empty —
     * it leaves the console standing on whatever `GET /group/tree` answers, which the
     * server has filtered by the same scope.
     *
     * Read with a raw call and the header written by hand, and not through the fetch
     * store — that store reads THIS one for its token, so a store that called it back
     * would be a cycle. It is the ONLY call in the portal that does so.
     */
    const loadUserData = async (): Promise<void> => {
        if (!JWT.value) return

        const response = await window.fetch('/api/pms/v1/me', {
            headers: { Authorization: `Bearer ${JWT.value}` },
        }).catch(() => null)

        if (!response?.ok) return

        const user = await response.json() as IGroupUser

        userData.value    = user
        permissions.value = user.permissions ?? []
        scope.value       = user.scope ?? null

        if (user.language && user.language !== locale.value) changeLocale(user.language)
    }

    /**
     * The one read of the account a page load makes.
     *
     * Two callers want it at the same moment on every reload — the router's guard, which
     * may not decide a permission question before the answer is in, and the shell, which
     * reads the account because a reload does not pass the sign-in screen.
     *
     * It never rejects: a console that cannot reach the service still has to render.
     */
    const ensureUserData = (): Promise<void> => {
        if (userData.value) return Promise.resolve()

        userDataInFlight ??= loadUserData()
            .catch(() => {})
            .finally(() => { userDataInFlight = null })

        return userDataInFlight
    }

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        i18n.global.locale.value = newLocale as 'sl' | 'en'
    }

    return {
        JWT, locale, userData, permissions, scope, loaderActive, colorMode,
        getJWT, isLoggedIn, isLoaderActive, isMobile, can, canAny,
        setLogin, setLogout, startLoading, stopLoading, changeLocale,
        loadUserData, ensureUserData,
    }
})
